import { useRef, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { DraftCarton } from '../draft'

export type CartonNavigatorProps = {
	cartons: DraftCarton[],
	activeKey: string | null,
	onSelect: ( key: string ) => void,
	onAdd: () => void,
}

function unitsIn( carton: DraftCarton ): number {
	return Object.values( carton.contents ).reduce( ( sum, quantity ) => sum + quantity, 0 )
}

/**
 * Moving between cartons.
 *
 * Only one carton is open at a time, so the step reads the same whether the run
 * fills three boxes or thirty. Stepping is the common move and gets the
 * standing buttons; jumping to a particular box is rarer and sits behind the
 * name.
 */
export default function CartonNavigator( { cartons, activeKey, onSelect, onAdd }: CartonNavigatorProps ) {
	const theme = useTheme()
	const anchorRef = useRef<HTMLButtonElement>( null )
	const [ open, setOpen ] = useState( false )

	const index = cartons.findIndex( ( carton ) => carton.key === activeKey )
	const step = ( delta: number ) => {
		const next = cartons[ index + delta ]
		if ( next ) {
			onSelect( next.key )
		}
	}

	return (
		<Stack direction="row" spacing={`${theme.touch.gap}px`} alignItems="center">
			<IconButton
				aria-label="Previous carton"
				disabled={index <= 0}
				onClick={() => step( -1 )}
				sx={{ border: 1, borderColor: 'pickFlow.cardBorder' }}
			>
				<ChevronLeftIcon />
			</IconButton>

			<Button
				ref={anchorRef}
				variant="outlined"
				color="inherit"
				onClick={() => setOpen( true )}
				endIcon={<ArrowDropDownIcon />}
				sx={{
					flex: 1,
					minHeight: theme.touch.control,
					borderColor: 'pickFlow.cardBorder',
					bgcolor: 'pickFlow.surface',
				}}
			>
				<Typography variant="body1" component="span" fontWeight={700}>
					Carton {index + 1}
				</Typography>
				<Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 0.75 }}>
					of {cartons.length}
				</Typography>
			</Button>

			<IconButton
				aria-label="Next carton"
				disabled={index < 0 || index >= cartons.length - 1}
				onClick={() => step( 1 )}
				sx={{ border: 1, borderColor: 'pickFlow.cardBorder' }}
			>
				<ChevronRightIcon />
			</IconButton>

			<IconButton
				aria-label="Add carton"
				color="primary"
				onClick={onAdd}
				sx={{ border: 1, borderStyle: 'dashed', borderColor: 'primary.main' }}
			>
				<AddIcon />
			</IconButton>

			<Menu anchorEl={anchorRef.current} open={open} onClose={() => setOpen( false )}>
				{cartons.map( ( carton, position ) => {
					const units = unitsIn( carton )
					return (
						<MenuItem
							key={carton.key}
							selected={carton.key === activeKey}
							onClick={() => {
								onSelect( carton.key )
								setOpen( false )
							}}
							sx={{ minHeight: theme.touch.target }}
						>
							<Box>
								<Typography variant="body1">Carton {position + 1}</Typography>
								<Typography variant="body2" color="text.secondary">
									{units === 0 ? 'Empty' : `${units} ${units === 1 ? 'unit' : 'units'}`}
									{carton.trackingNumber.trim() ? ' · Tracked' : ''}
								</Typography>
							</Box>
						</MenuItem>
					)
				} )}
			</Menu>
		</Stack>
	)
}
